import { Button } from '@material-tailwind/react';
import { createValidityState } from 'hooks/forms';
import React, { useState, useRef, useEffect } from 'react';

interface AlphaSelectMultipleProps {
  name: string;
  options: string[];
  value: string[];
  onChange: (selected: string[]) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  onValidate?: (validity: { valid: boolean }) => void | string;
}

export default function AlphaSelectMultiple({
  name,
  options,
  value,
  onChange,
  required,
  placeholder = 'Select option(s)',
  className = '',
  onValidate,
}: AlphaSelectMultipleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [interaction, setInteraction] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  type Value = string | number | readonly string[] | undefined;
  const [state, setState] = useState<Value>(() => value ?? []);

  const toggleOption = (option: string) => {
    const newValue = value.includes(option)
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  const displayText = value.length > 0 ? value.join(', ') : placeholder;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine whether to open upward or downward
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const DROPDOWN_HEIGHT = 250; // or whatever your max dropdown height is

      setDropUp(spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  const validity = createValidityState({
    valueMissing: Boolean(
      required && (!Array.isArray(state) || state.length === 0),
    ),
  });
  const errorMessage = onValidate?.(validity) ?? '';
  useEffect(() => {
    inputRef.current?.setCustomValidity(errorMessage);
  }, [errorMessage]);

  return (
    <div ref={containerRef} className="relative">
      {value.length === 0 && (
        <input
          type="text"
          hidden
          required
          name={name}
          value=""
          onInvalid={() => setInteraction(true)}
          onChange={(event) => setState(event.currentTarget.value)}
          ref={inputRef}
        />
      )}
      {value.map((val, idx) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={val + idx}
          type="hidden"
          name={name.replace(/\[\]$/, '')}
          value={val}
        />
      ))}
      <Button
        variant="outlined"
        ref={buttonRef}
        type="button"
        fullWidth
        color={interaction && errorMessage ? 'red' : 'gray'}
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className={`flex items-center justify-between rounded-[7px] alpha-body-2 ${className} ${
          interaction && errorMessage
            ? 'border-highlights/red-dark bg-highlights/red-light text-red-500'
            : ''
        }`}
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
      >
        <span
          className={`truncate alpha-form-input ${value.length === 0 ? '!text-gray-700' : ''}`}
        >
          {displayText}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`size-5 transition-transform duration-200 text-text/primary${isOpen ? 'rotate-180' : ''}`}
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          ></path>
        </svg>
      </Button>

      {isOpen && (
        <div
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className={`absolute z-50 max-h-64 w-full overflow-y-auto rounded-md border border-gray-300 px-[--spacing-padding-x-sm] py-[--spacing-padding-y-sm] shadow-md bg-white ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} `}
        >
          {options.map((option) => (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              key={option}
              onClick={() => {
                toggleOption(option);
                setInteraction(false);
              }}
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className={`my-1 flex cursor-pointer items-center justify-between rounded-[--radius-button] px-3 py-2 text-gray-700 alpha-form-input ${
                value.includes(option) ? 'bg-[--fill-alternative-1]' : ''
              }`}
            >
              <span>{option}</span>
              {value.includes(option) && (
                <span className="flex w-5 shrink-0 justify-end">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="size-5 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {interaction && errorMessage && (
        <div className="px-padding/x-sm pt-1 text-highlights/red-dark alpha-caption">
          {errorMessage}
        </div>
      )}
    </div>
  );
}
